import React from "react";
import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";

import { ProbationFormPanel } from "../../assessment/components/probation-form-panel.tsx";
import { ProbationReadOnlyPanel } from "../../assessment/components/probation-readonly-panel.tsx";
import { Modal } from "../../../ui/modal/modal.tsx";
import { Badge, Spinner } from "../../../ui/index.ts";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../ui/tabs/tabs.tsx";
import { useUser } from "../../../../lib/rbac/useUser.ts";
import {
  resolveEditableSectionType,
  resolveReviewMode,
} from "../utils/resolve-editable-section-type.ts";
import { probationStatusBadgeVariant } from "../utils/probation-status-badge.ts";
import type { ProbationCycleRow } from "../schemas/probation-cycle-list.schema.ts";
import { capitalizeWords } from "../../../../utils/format-display-text.ts";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";

import styles from "./probation-review-modal.module.css";

const TAB_EMPLOYEE = "employee";
const TAB_MANAGER = "manager";
const TAB_HR = "hr";

export type ProbationReviewModalProps = {
  open: boolean;
  onClose: () => void;
  cycle: ProbationCycleRow | null;
  isReportingManager: boolean;
  onSuccess: () => void;
};

function formatStatusLabel(status: string | undefined): string {
  if (status == null || status === "") return "Unknown";
  return capitalizeWords(status.replace(/_/g, " "));
}

function parseCycleId(cycle: ProbationCycleRow): number {
  return Number(cycle.cycleId);
}

export function ProbationReviewModal({
  open,
  onClose,
  cycle,
  isReportingManager,
  onSuccess,
}: ProbationReviewModalProps) {
  const user = useUser();
  const isSqeLabs = useSqeLabs();
  const managerSectionType = isSqeLabs ? "SQE_MANAGER" : "MANAGER";
  const roles = user?.roles ?? [];
  const isManagerSubmissionPending = cycle?.managerSubmitted !== true;
  const reviewMode = resolveReviewMode(roles, cycle?.managerSubmitted, isReportingManager);
  const editableSectionType = resolveEditableSectionType(
    roles,
    cycle?.managerSubmitted,
    isReportingManager,
  );
  const [activeTab, setActiveTab] = useState<string>(TAB_EMPLOYEE);

  useEffect(() => {
    if (!open) {
      setActiveTab(TAB_EMPLOYEE);
    }
  }, [open]);

  const cycleId = useMemo(() => (cycle != null ? parseCycleId(cycle) : null), [cycle]);

  if (cycle == null || cycleId == null || !Number.isFinite(cycleId)) return null;

  const employeeName = cycle.employeeName ?? "Probation Review";
  const shouldShowHrForm =
    reviewMode === "hr" &&
    editableSectionType === "HR" &&
    !isManagerSubmissionPending;
  const shouldShowHrPendingReadOnly =
    reviewMode === "hr" &&
    editableSectionType === "HR" &&
    isManagerSubmissionPending;

  const handleFormSuccess = () => {
    onSuccess();
  };

  const readOnlyTabs = (
    <Tabs value={activeTab} onValueChange={setActiveTab} className={styles.readOnlyTabs}>
      <TabsList className={styles.tabsList}>
        <TabsTrigger value={TAB_EMPLOYEE}>Employee</TabsTrigger>
        <TabsTrigger value={TAB_MANAGER}>Manager</TabsTrigger>
        {reviewMode === "superadmin" ? (
          <TabsTrigger value={TAB_HR}>HR</TabsTrigger>
        ) : null}
      </TabsList>
      <TabsContent value={TAB_EMPLOYEE} className={styles.tabPanel}>
        <ProbationReadOnlyPanel
          key={`employee-${cycleId}`}
          cycleId={cycleId}
          sectionType="EMPLOYEE"
          title="Employee Assessment"
        />
      </TabsContent>
      <TabsContent value={TAB_MANAGER} className={styles.tabPanel}>
        <ProbationReadOnlyPanel
          key={`manager-${cycleId}`}
          cycleId={cycleId}
          sectionType={managerSectionType}
          title="Manager Assessment"
        />
      </TabsContent>
      {reviewMode === "superadmin" ? (
        <TabsContent value={TAB_HR} className={styles.tabPanel}>
          <ProbationReadOnlyPanel
            key={`hr-${cycleId}`}
            cycleId={cycleId}
            sectionType="HR"
            title="HR Assessment"
          />
        </TabsContent>
      ) : null}
    </Tabs>
  );

  const hrReadOnlyTabs = (
    <Tabs value={activeTab} onValueChange={setActiveTab} className={styles.readOnlyTabs}>
      <TabsList className={styles.tabsList}>
        <TabsTrigger value={TAB_EMPLOYEE}>Employee</TabsTrigger>
        <TabsTrigger value={TAB_MANAGER}>Manager</TabsTrigger>
      </TabsList>
      <TabsContent value={TAB_EMPLOYEE} className={styles.tabPanel}>
        <ProbationReadOnlyPanel
          key={`employee-${cycleId}`}
          cycleId={cycleId}
          sectionType="EMPLOYEE"
          title="Employee Assessment"
        />
      </TabsContent>
      <TabsContent value={TAB_MANAGER} className={styles.tabPanel}>
        <ProbationReadOnlyPanel
          key={`manager-${cycleId}`}
          cycleId={cycleId}
          sectionType={managerSectionType}
          title="Manager Assessment"
        />
      </TabsContent>
    </Tabs>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={employeeName}
      size="lg"
      className={shouldShowHrPendingReadOnly ? undefined : styles.panel}
    >
      {reviewMode === "superadmin" ? (
        readOnlyTabs
      ) : reviewMode === "manager" && editableSectionType === "MANAGER" ? (
        <div className={styles.splitGrid}>
          <div className={styles.splitCol}>
            <ProbationFormPanel
              key={`form-manager-${cycleId}`}
              cycleId={cycleId}
              sectionType={managerSectionType}
              onSuccess={handleFormSuccess}
            />
          </div>
          <div className={clsx(styles.splitCol, styles.splitColReadOnly)}>
            <ProbationReadOnlyPanel
              key={`readonly-employee-${cycleId}`}
              cycleId={cycleId}
              sectionType="EMPLOYEE"
              title="Employee Assessment"
            />
          </div>
        </div>
      ) : shouldShowHrForm ? (
        <div className={styles.splitGrid}>
          <div className={styles.splitCol}>
            <ProbationFormPanel
              key={`form-hr-${cycleId}`}
              cycleId={cycleId}
              sectionType="HR"
              onSuccess={handleFormSuccess}
            />
          </div>
          <div className={clsx(styles.splitCol, styles.splitColReadOnly)}>{hrReadOnlyTabs}</div>
        </div>
      ) : shouldShowHrPendingReadOnly ? (
        hrReadOnlyTabs
      ) : (
        <div className={styles.loadingWrap}>
          <Spinner />
        </div>
      )}
    </Modal>
  );
}
